import OrganizerDatePicker, {
  formatDateInputValue,
  isDateRangeInvalid,
} from "../../../../../shared/components/OrganizerDatePicker";
import Spinner from "../../../../../shared/components/Spinner";
import { getSiteLanguage } from "../../../../../shared/i18n/language";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import {
  OrganizerStatistics,
  OrganizerStatisticsByType,
  OrganizerStatisticsPeriod,
  OrganizerStatisticsPoint,
} from "../../../shared/services/organizerStatisticsService";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

export type OrganizerChartPeriod =
  | "last_month"
  | "last_6_months"
  | "last_year"
  | "all_time";

const ORGANIZER_CHART_PERIOD_OPTIONS: Array<{
  value: OrganizerChartPeriod;
  label: string;
}> = [
  {
    value: "last_month",
    label: siteTexts.profile.user.organizerStatistics.chartPeriods.lastMonth,
  },
  {
    value: "last_6_months",
    label:
      siteTexts.profile.user.organizerStatistics.chartPeriods.lastSixMonths,
  },
  {
    value: "last_year",
    label: siteTexts.profile.user.organizerStatistics.chartPeriods.lastYear,
  },
  {
    value: "all_time",
    label: siteTexts.profile.user.organizerStatistics.chartPeriods.allTime,
  },
];

const LOCALE_BY_LANGUAGE = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
} as const;

type ProfileOrganizerStatisticsTabProps = {
  userRole: "admin" | "organizer";
  open: boolean;
  period: OrganizerStatisticsPeriod;
  dateFrom: string;
  dateTo: string;
  loading: boolean;
  error: string;
  stats: OrganizerStatistics | null;
  chartStats: OrganizerStatistics | null;
  chartStatsLoading: boolean;
  chartStatsError: string;
  chartPeriod: OrganizerChartPeriod;
  onToggle: () => void;
  onPeriodChange: (period: OrganizerStatisticsPeriod) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onChartPeriodChange: (period: OrganizerChartPeriod) => void;
};

function currentLocale() {
  return LOCALE_BY_LANGUAGE[getSiteLanguage()] || "pt-BR";
}

function formatMoneyCents(amountCents: number, currency = "BRL") {
  return new Intl.NumberFormat(currentLocale(), {
    style: "currency",
    currency: String(currency || "BRL").toUpperCase(),
  }).format(Number(amountCents || 0) / 100);
}

function organizerEventTypeLabel(type: OrganizerStatisticsByType["type"]) {
  const labels = siteTexts.profile.user.organizerStatistics.eventTypes;
  if (type === "super8") return labels.super8;
  if (type === "poco") return labels.poco;
  if (type === "tournament") return labels.tournament;
  return labels.challenge;
}

function formatOrganizerStatValue(value: number | string) {
  if (typeof value === "number") {
    return new Intl.NumberFormat(currentLocale()).format(value);
  }
  return String(value || "-");
}

function addMonths(date: Date, amount: number) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount);
  return next;
}

export function organizerChartPeriodQuery(period: OrganizerChartPeriod): {
  period: OrganizerStatisticsPeriod;
  dateFrom?: string;
  dateTo?: string;
} {
  const today = new Date();
  if (period === "last_month") {
    return { period: "last_30" };
  }
  if (period === "last_6_months") {
    return {
      period: "custom",
      dateFrom: formatDateInputValue(addMonths(today, -6)),
      dateTo: formatDateInputValue(today),
    };
  }
  if (period === "last_year") {
    return {
      period: "custom",
      dateFrom: formatDateInputValue(addMonths(today, -12)),
      dateTo: formatDateInputValue(today),
    };
  }
  return { period: "all" };
}

function pointQuarterLabel(label: string) {
  const [yearRaw, monthRaw] = String(label || "").split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1) {
    return label;
  }
  return `${year} T${Math.ceil(month / 3)}`;
}

function chartPointDisplayLabel(label: string) {
  const [yearRaw, monthRaw] = String(label || "").split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1) {
    return label;
  }
  const months = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  return `${months[month - 1]}/${String(year).slice(-2)}`;
}

function groupPointsByQuarter(points: OrganizerStatisticsPoint[]) {
  const grouped = new Map<string, number>();
  points.forEach((point) => {
    const label = pointQuarterLabel(point.label);
    grouped.set(
      label,
      Number(grouped.get(label) || 0) + Number(point.value || 0),
    );
  });
  return Array.from(grouped.entries()).map(([label, value]) => ({
    label,
    value,
  }));
}

function chartPointsForPeriod(
  points: OrganizerStatisticsPoint[],
  period: OrganizerChartPeriod,
  variant: "bar" | "line" | "ranking",
) {
  if (variant === "ranking") return points.slice(0, 6);
  const visible =
    period === "last_month"
      ? points.slice(-6)
      : period === "last_6_months"
        ? points.slice(-6)
        : period === "last_year"
          ? points.slice(-12)
          : points.length > 24
            ? groupPointsByQuarter(points)
            : points;
  return visible.map((point) => ({
    ...point,
    label:
      period === "all_time" && points.length > 24
        ? point.label
        : chartPointDisplayLabel(point.label),
  }));
}

function StatsPeriodFilter({
  value,
  onChange,
  disabled,
}: {
  value: OrganizerChartPeriod;
  onChange: (period: OrganizerChartPeriod) => void;
  disabled?: boolean;
}) {
  return (
    <label className="organizer-stat-chart-period">
      <span className="sr-only">Período do gráfico</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) =>
          onChange(event.target.value as OrganizerChartPeriod)
        }
      >
        {ORGANIZER_CHART_PERIOD_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="material-symbols-outlined" aria-hidden="true">
        expand_more
      </span>
    </label>
  );
}

function OrganizerMiniChart({
  icon,
  title,
  subtitle,
  points,
  period,
  onPeriodChange,
  loading = false,
  money = false,
  variant = "bar",
  emptyMessage,
  metricLabel,
}: {
  icon: string;
  title: string;
  subtitle: string;
  points: OrganizerStatisticsPoint[];
  period: OrganizerChartPeriod;
  onPeriodChange: (period: OrganizerChartPeriod) => void;
  loading?: boolean;
  money?: boolean;
  variant?: "bar" | "line" | "ranking";
  emptyMessage?: string;
  metricLabel: string;
}) {
  const visiblePoints = chartPointsForPeriod(points, period, variant);
  const max = Math.max(
    1,
    ...visiblePoints.map((point) => Number(point.value || 0)),
  );
  const hasData = visiblePoints.some((point) => Number(point.value || 0) > 0);
  const formatValue = (value: number) =>
    money ? formatMoneyCents(value) : formatOrganizerStatValue(value);
  const displayEmptyMessage =
    emptyMessage ||
    siteTexts.profile.user.organizerStatistics.chartEmptyMessages.noData;
  const displayPeriodLabel =
    variant === "ranking"
      ? siteTexts.profile.user.organizerStatistics.chartPeriods.allPeriods
      : siteTexts.profile.user.organizerStatistics.chartPeriods.lastSixMonths;
  const linePoints = visiblePoints.map((point, index) => {
    const value = Number(point.value || 0);
    const x =
      visiblePoints.length > 1
        ? 40 + (index / (visiblePoints.length - 1)) * 520
        : 300;
    const y = 132 - (value / max) * 104;
    return {
      ...point,
      value,
      x,
      y,
      formattedValue: formatValue(value),
    };
  });
  const linePath = linePoints
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const areaPath = linePoints.length
    ? `${linePath} L ${linePoints[linePoints.length - 1].x} 144 L ${linePoints[0].x} 144 Z`
    : "";

  return (
    <section
      className={`organizer-stat-chart organizer-stat-chart--${variant}`}
      data-period-label={displayPeriodLabel}
    >
      <div className="organizer-stat-chart-head">
        <span
          className="organizer-stat-chart-icon material-symbols-outlined"
          aria-hidden="true"
        >
          {icon}
        </span>
        <div>
          <h4>{title}</h4>
          <p>{subtitle}</p>
        </div>
        <StatsPeriodFilter
          value={period}
          onChange={onPeriodChange}
          disabled={loading}
        />
      </div>

      {!hasData ? (
        <div className="organizer-stat-chart-empty">
          <span className="material-symbols-outlined" aria-hidden="true">
            monitoring
          </span>
          <p>{displayEmptyMessage}</p>
        </div>
      ) : variant === "ranking" ? (
        <div className="organizer-stat-ranking-chart">
          {visiblePoints.map((point, index) => {
            const value = Number(point.value || 0);
            const formattedValue = formatValue(value);
            const tooltip = `${point.label}: ${formattedValue} (${metricLabel})`;
            const suffix = value === 1 ? "inscrito" : "inscritos";
            return (
              <div
                className="organizer-stat-ranking-chart-row"
                key={`${point.label}-${index}`}
                title={tooltip}
                aria-label={tooltip}
              >
                <strong>{point.label}</strong>
                <div className="organizer-stat-ranking-chart-track">
                  <span
                    style={{
                      width: `${Math.max(8, (value / max) * 100)}%`,
                    }}
                  />
                </div>
                <span>
                  <b>{formattedValue}</b>
                  <small>{suffix}</small>
                </span>
              </div>
            );
          })}
        </div>
      ) : variant === "line" ? (
        <div className="organizer-stat-chart-line-plot">
          <div className="organizer-stat-chart-y-axis" aria-hidden="true">
            <span>{formatValue(max)}</span>
            <span>{formatValue(Math.round(max / 2))}</span>
            <span>{formatMoneyCents(0)}</span>
          </div>
          <div className="organizer-stat-line-chart">
            <svg viewBox="0 0 600 170" role="img" aria-label={title}>
              <defs>
                <linearGradient
                  id="organizerRevenueLine"
                  x1="0"
                  x2="1"
                  y1="0"
                  y2="0"
                >
                  <stop offset="0%" stopColor="var(--color-brand-pink)" />
                  <stop offset="100%" stopColor="var(--color-brand-blue)" />
                </linearGradient>
                <linearGradient
                  id="organizerRevenueArea"
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor="var(--color-brand-pink)"
                    stopOpacity="0.34"
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-brand-blue)"
                    stopOpacity="0.04"
                  />
                </linearGradient>
              </defs>
              <g className="organizer-stat-line-grid" aria-hidden="true">
                <line x1="28" x2="584" y1="28" y2="28" />
                <line x1="28" x2="584" y1="80" y2="80" />
                <line x1="28" x2="584" y1="132" y2="132" />
              </g>
              <path className="organizer-stat-line-area" d={areaPath} />
              <path className="organizer-stat-line-path" d={linePath} />
              {linePoints.map((point, index) => {
                const tooltip = `${point.label}: ${point.formattedValue} (${metricLabel})`;
                return (
                  <g key={`${point.label}-${index}`}>
                    <title>{tooltip}</title>
                    <text
                      className="organizer-stat-line-value"
                      x={point.x}
                      y={Math.max(14, point.y - 12)}
                    >
                      {point.formattedValue}
                    </text>
                    <circle
                      className="organizer-stat-line-point"
                      cx={point.x}
                      cy={point.y}
                      r="5"
                    />
                  </g>
                );
              })}
            </svg>
            <div className="organizer-stat-line-labels">
              {linePoints.map((point, index) => (
                <small key={`${point.label}-label-${index}`}>{point.label}</small>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="organizer-stat-chart-plot">
          <div className="organizer-stat-chart-y-axis" aria-hidden="true">
            <span>{formatValue(max)}</span>
            <span>{formatValue(Math.round(max / 2))}</span>
            <span>{money ? formatMoneyCents(0) : "0"}</span>
          </div>
          <div className="organizer-stat-chart-bars">
            {visiblePoints.map((point, index) => {
              const value = Number(point.value || 0);
              const formattedValue = formatValue(value);
              const tooltip = `${point.label}: ${formattedValue} (${metricLabel})`;
              return (
                <div
                  className="organizer-stat-chart-bar"
                  key={`${point.label}-${index}`}
                  title={tooltip}
                  aria-label={tooltip}
                >
                  <strong>{formattedValue}</strong>
                  <div className="organizer-stat-chart-track">
                    <span
                      style={{
                        height: `${Math.max(6, (value / max) * 100)}%`,
                      }}
                    />
                  </div>
                  <small>{point.label}</small>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ProfileOrganizerStatisticsTab({
  userRole,
  open,
  period,
  dateFrom,
  dateTo,
  loading,
  error,
  stats,
  chartStats,
  chartStatsLoading,
  chartStatsError,
  chartPeriod,
  onToggle,
  onPeriodChange,
  onDateFromChange,
  onDateToChange,
  onChartPeriodChange,
}: ProfileOrganizerStatisticsTabProps) {
  const organizerChartsSource = chartStats || stats;

  return (
    <ProfileAccordionSection
      id="profile-statistics"
      icon="monitoring"
      title={siteTexts.profile.user.organizerStatistics.title}
      subtitle={siteTexts.profile.user.organizerStatistics.description}
      open={open}
      onToggle={onToggle}
    >
      <section className="user-profile-organizer-stats">
        <UserProfileSectionHeader
          className="user-profile-achievements-header"
          icon="monitoring"
          title={siteTexts.profile.user.organizerStatistics.title}
          description={siteTexts.profile.user.organizerStatistics.description}
          actions={
            userRole === "admin" ? (
              <span className="user-profile-achievements-count">
                {siteTexts.profile.user.organizerStatistics.adminScope}
              </span>
            ) : null
          }
        />

        <div className="organizer-stat-filters">
          {(
            ["all", "current_year", "last_30", "last_90", "custom"] as OrganizerStatisticsPeriod[]
          ).map((item) => (
            <button
              type="button"
              key={item}
              className={period === item ? "is-active" : ""}
              onClick={() => onPeriodChange(item)}
            >
              {siteTexts.profile.user.organizerStatistics.periods[item]}
            </button>
          ))}
          {period === "custom" && (
            <div className="organizer-stat-custom-period">
              <OrganizerDatePicker
                label={siteTexts.profile.user.organizerStatistics.dateFrom}
                value={dateFrom}
                onChange={onDateFromChange}
                disabledAfter={dateTo}
              />
              <OrganizerDatePicker
                label={siteTexts.profile.user.organizerStatistics.dateTo}
                value={dateTo}
                onChange={onDateToChange}
                disabledBefore={dateFrom}
              />
              {isDateRangeInvalid(dateFrom, dateTo) && (
                <p className="organizer-stat-date-error">
                  {
                    siteTexts.profile.user.organizerStatistics
                      .invalidDateRange
                  }
                </p>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="user-profile-achievements-loading">
            <Spinner />
            <span>{siteTexts.profile.user.organizerStatistics.loading}</span>
          </div>
        ) : error ? (
          <p className="status-message">
            {siteTexts.profile.user.organizerStatistics.loadError}
          </p>
        ) : stats ? (
          <>
            <div className="organizer-stat-card-grid">
              {[
                {
                  icon: "emoji_events",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .completedEvents,
                  value: stats.totals.completedEvents,
                },
                {
                  icon: "event_upcoming",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .futureEvents,
                  value: stats.totals.futureEvents,
                },
                {
                  icon: "groups",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .uniqueAthletes,
                  value: stats.totals.uniqueAthletes,
                },
                {
                  icon: "confirmation_number",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .totalRegistrations,
                  value: stats.totals.totalRegistrations,
                },
                {
                  icon: "verified",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .soldOutEvents,
                  value: stats.totals.soldOutEvents,
                },
                {
                  icon: "payments",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .estimatedRevenue,
                  value: formatMoneyCents(stats.totals.revenueCents),
                },
                {
                  icon: "analytics",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .averageAthletes,
                  value: stats.totals.averageAthletesPerEvent,
                },
                {
                  icon: "category",
                  label:
                    siteTexts.profile.user.organizerStatistics.cards
                      .popularCategory,
                  value: stats.totals.mostPopularCategory || "-",
                },
              ].map((card) => (
                <article className="organizer-stat-card" key={card.label}>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {card.icon}
                  </span>
                  <div>
                    <strong>{formatOrganizerStatValue(card.value)}</strong>
                    <small>{card.label}</small>
                  </div>
                </article>
              ))}
            </div>

            <div className="organizer-stat-split">
              <section className="organizer-stat-panel">
                <h3>{siteTexts.profile.user.organizerStatistics.byTypeTitle}</h3>
                <div className="organizer-stat-type-list">
                  {stats.byType.map((item) => (
                    <article className="organizer-stat-type-card" key={item.type}>
                      <div>
                        <strong>{organizerEventTypeLabel(item.type)}</strong>
                        <span>{formatMoneyCents(item.revenueCents)}</span>
                      </div>
                      <dl>
                        <div>
                          <dt>
                            {
                              siteTexts.profile.user.organizerStatistics.metrics
                                .completed
                            }
                          </dt>
                          <dd>{item.completedEvents}</dd>
                        </div>
                        <div>
                          <dt>
                            {
                              siteTexts.profile.user.organizerStatistics.metrics
                                .registrations
                            }
                          </dt>
                          <dd>{item.totalRegistrations}</dd>
                        </div>
                        <div>
                          <dt>
                            {
                              siteTexts.profile.user.organizerStatistics.metrics
                                .average
                            }
                          </dt>
                          <dd>{item.averageParticipants}</dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              </section>

              <section className="organizer-stat-panel">
                <h3>
                  {
                    siteTexts.profile.user.organizerStatistics
                      .internalRankingTitle
                  }
                </h3>
                <div className="organizer-stat-ranking-list">
                  {[
                    {
                      label:
                        siteTexts.profile.user.organizerStatistics.ranking
                          .mostParticipations,
                      item: stats.internalRanking.mostParticipations,
                    },
                    {
                      label:
                        siteTexts.profile.user.organizerStatistics.ranking
                          .mostTitles,
                      item: stats.internalRanking.mostTitles,
                    },
                    {
                      label:
                        siteTexts.profile.user.organizerStatistics.ranking
                          .mostRecurring,
                      item: stats.internalRanking.mostRecurring,
                    },
                    {
                      label:
                        siteTexts.profile.user.organizerStatistics.ranking
                          .topCategory,
                      item: stats.internalRanking.mostRegisteredCategory,
                    },
                    {
                      label:
                        siteTexts.profile.user.organizerStatistics.ranking
                          .topEvent,
                      item: stats.internalRanking.eventWithMostRegistrations,
                    },
                  ].map((row) => (
                    <div className="organizer-stat-ranking-row" key={row.label}>
                      <span>{row.label}</span>
                      <strong>
                        {row.item?.label ||
                          siteTexts.profile.user.organizerStatistics.emptyValue}
                      </strong>
                      <small>
                        {row.item
                          ? formatOrganizerStatValue(row.item.value)
                          : "-"}
                      </small>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {chartStatsError && (
              <p className="status-message">{chartStatsError}</p>
            )}
            <div
              className={`organizer-stat-charts ${
                chartStatsLoading ? "is-loading" : ""
              }`}
            >
              <OrganizerMiniChart
                icon="monitoring"
                title={
                  siteTexts.profile.user.organizerStatistics.charts
                    .registrationsByMonth
                }
                subtitle={
                  siteTexts.profile.user.organizerStatistics.chartDescriptions
                    .registrationsByMonth
                }
                points={
                  organizerChartsSource?.charts.registrationsByMonth || []
                }
                period={chartPeriod}
                onPeriodChange={onChartPeriodChange}
                loading={chartStatsLoading}
                metricLabel={
                  siteTexts.profile.user.organizerStatistics.chartMetricLabels
                    .registrationsByMonth
                }
              />
              <OrganizerMiniChart
                icon="request_quote"
                title={
                  siteTexts.profile.user.organizerStatistics.charts
                    .revenueByMonth
                }
                subtitle={
                  siteTexts.profile.user.organizerStatistics.chartDescriptions
                    .revenueByMonth
                }
                points={organizerChartsSource?.charts.revenueByMonth || []}
                period={chartPeriod}
                onPeriodChange={onChartPeriodChange}
                loading={chartStatsLoading}
                variant="line"
                metricLabel={
                  siteTexts.profile.user.organizerStatistics.chartMetricLabels
                    .revenueByMonth
                }
                emptyMessage={
                  siteTexts.profile.user.organizerStatistics.chartEmptyMessages
                    .noRevenue
                }
                money
              />
              <OrganizerMiniChart
                icon="event"
                title={
                  siteTexts.profile.user.organizerStatistics.charts
                    .eventsByMonth
                }
                subtitle={
                  siteTexts.profile.user.organizerStatistics.chartDescriptions
                    .eventsByMonth
                }
                points={organizerChartsSource?.charts.eventsByMonth || []}
                period={chartPeriod}
                onPeriodChange={onChartPeriodChange}
                loading={chartStatsLoading}
                metricLabel={
                  siteTexts.profile.user.organizerStatistics.chartMetricLabels
                    .eventsByMonth
                }
              />
              <OrganizerMiniChart
                icon="emoji_events"
                title={
                  siteTexts.profile.user.organizerStatistics.charts.categories
                }
                subtitle={
                  siteTexts.profile.user.organizerStatistics.chartDescriptions
                    .categories
                }
                points={organizerChartsSource?.charts.categories || []}
                period={chartPeriod}
                onPeriodChange={onChartPeriodChange}
                loading={chartStatsLoading}
                variant="ranking"
                metricLabel={
                  siteTexts.profile.user.organizerStatistics.chartMetricLabels
                    .categories
                }
              />
            </div>
            <p className="organizer-stat-chart-note">
              {siteTexts.profile.user.organizerStatistics.chartNote}
            </p>
          </>
        ) : null}
      </section>
    </ProfileAccordionSection>
  );
}
